/**
 * 04-10-2026 (Jordy): de chatverbinding per model en de "Resend"-regel.
 *
 * De server bepaalt beide (src/services/chat_verbinding.py); dit bestand houdt
 * alleen het scherm in de pas. De lijst met "aantoonbaar onverzonden"-meldingen
 * moet gelijk blijven aan die in de server — de server weigert een Resend toch
 * als hij er niet in valt, dus een afwijking hier geeft hooguit een knop die
 * een nette weigering oplevert, nooit een dubbel bericht.
 */

/** Een fout die eindigt op deze zin: er is aantoonbaar niets verstuurd. */
const ONVERZONDEN_SLOT = 'Nothing was sent.'

/** Oude meldingen met dezelfde betekenis (geen socket / geen luisteraar). */
const ONVERZONDEN_EXACT = new Set<string>([
  'FanCentro agency session is not connected',
  'No listener found for model',
  'No live FanCentro listener was found for this model.',
])

/** Bewijst deze foutmelding dat het bericht het platform nooit bereikte? */
export function isAantoonbaarOnverzonden(fout: string | null | undefined): boolean {
  const tekst = String(fout ?? '').trim()
  if (!tekst) return false
  if (tekst.endsWith(ONVERZONDEN_SLOT)) return true
  if (ONVERZONDEN_EXACT.has(tekst)) return true
  return tekst.startsWith('No listener found for model')
}

/** "FanCentro" of "F2F". */
export function platformLabel(platform: string | null | undefined): string {
  return String(platform ?? '').toLowerCase() === 'fancentro' ? 'FanCentro' : 'F2F'
}

/** Tijd van de laatste melding in lokale tijd: "14:05", of "03-10 14:05"
 *  als het niet vandaag was. Tijden zonder zone zijn UTC. */
export function sindsTijd(iso: string | null | undefined): string {
  if (!iso) return '?'
  const v = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso.replace(' ', 'T')}Z`
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return '?'
  const p = (n: number) => String(n).padStart(2, '0')
  const tijd = `${p(d.getHours())}:${p(d.getMinutes())}`
  const nu = new Date()
  const vandaag = d.getFullYear() === nu.getFullYear()
    && d.getMonth() === nu.getMonth() && d.getDate() === nu.getDate()
  return vandaag ? tijd : `${p(d.getDate())}-${p(d.getMonth() + 1)} ${tijd}`
}

/**
 * De tekst van de rode balk boven het invoerveld.
 *
 * 31-10-2026 (Jordy): bij F2F zien alleen owner/admin (`beheer`) sinds wanneer
 * en waarom; andere rollen alleen dat de sync vertraagd is (de server stuurt
 * hun het tijdstip ook niet mee, zie src/services/f2f_neutraal.py).
 */
export function verbindingBalkTekst(
  platform: string | null | undefined,
  model: string,
  sinds: string | null | undefined,
  beheer = true,
): string {
  if (String(platform ?? '').toLowerCase() !== 'fancentro' && !beheer) {
    return `F2F sync for ${model} is delayed.`
  }
  const tijd = sindsTijd(sinds)
  if (String(platform ?? '').toLowerCase() === 'fancentro') {
    return `FanCentro connection for ${model} is down since ${tijd}. `
      + 'New messages wait up to 3 minutes and go out automatically when it is back; '
      + 'after that they fail and can be resent.'
  }
  return `F2F chat sync for ${model} has not succeeded since ${tijd}. `
    + 'New fan messages may arrive late and sending may fail.'
}
